/* earth-globe.js — reference-quality interactive 3D Earth globe for Timbrica.
 *
 * Loads Three.js r160 (ESM, self-hosted) via dynamic import, builds a layered
 * photoreal globe (earth + clouds + atmosphere + starfield), an astronomically
 * correct real-time day/night terminator (subsolar point from current UTC, see
 * earth-globe-astro.js), hand-rolled orbit controls (the camera orbits a fixed
 * Earth so geography stays put and the Sun is always correct), search + fly-to,
 * live ISS + earthquake layers, Moon, analemma, capability tiering and a
 * no-WebGL fallback. All copy is honest: no privacy / no-ads claims.
 */
(function () {
  'use strict';
  var CFG = window.GLOBE_CFG;
  var A = window.GlobeAstro;
  if (!CFG || !A) return;
  var T = CFG.t || {};
  function tt(k, def) { return (T[k] != null && T[k] !== '') ? T[k] : (def != null ? def : k); }

  // ── DOM ─────────────────────────────────────────────────────────────────
  var $ = function (id) { return document.getElementById(id); };
  var stage = $('glbStage'), canvas = $('glbCanvas'), loading = $('glbLoading'),
    fallback = $('glbFallback'), readout = $('glbReadout'), liveBox = $('glbLiveBox'),
    tooltip = $('glbTooltip'), srLive = $('glbLive'), fsClose = $('glbFsClose');
  if (!stage || !canvas) return;

  var reduceMotion = window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches;
  var rtl = CFG.dir === 'rtl';

  // ── Telemetry ───────────────────────────────────────────────────────────
  // Maps the tool's semantic event names onto the site's standard __ae types
  // (feature_use / tool_complete / tool_error / tool_diagnostic) so each event
  // gets device-class enrichment and lands in the /pino funnel dashboards. The
  // slug is set server-side via window.__TOOL_SLUG; no Timbrica3D (that's the
  // 3D-printing helper, not telemetry).
  var AE_MAP = {
    globe_ready: ['feature_use', { f: 'globe_ready' }],
    globe_first_interact: ['feature_use', { f: 'first_interact' }],
    globe_layer_toggle: ['feature_use', { f: 'layer' }],
    globe_style: ['feature_use', { f: 'style' }],
    globe_search_flyto: ['feature_use', { f: 'search_flyto' }],
    globe_locate: ['feature_use', { f: 'locate' }],
    globe_screenshot: ['tool_complete', { f: 'screenshot' }],
    globe_permalink: ['tool_complete', { f: 'permalink' }],
    globe_quality_degraded: ['tool_diagnostic', { f: 'quality_degraded' }],
    globe_webgl_unsupported: ['tool_error', { error_type: 'webgl_unsupported' }],
    globe_engine_failed: ['tool_error', { error_type: 'engine_load_failed' }]
  };
  var _aeFirst = false;
  function ae(ev, data) {
    var m = AE_MAP[ev];
    var type = m ? m[0] : 'feature_use';
    var payload = Object.assign({}, m ? m[1] : { f: ev }, data || {});
    try { window.__ae && window.__ae(type, payload); } catch (e) {}
  }
  function aeFirstInteract() { if (_aeFirst) return; _aeFirst = true; ae('globe_first_interact', {}); }

  function announce(msg) {
    if (srLive) srLive.textContent = msg;
    try { window.__announce && window.__announce(msg); } catch (e) {}
  }
  function toast(msg, type) { try { window.__toast ? window.__toast(msg, type) : 0; } catch (e) {} }

  // ── Capability detection / tiering ──────────────────────────────────────
  function hasWebGL2() {
    try {
      var c = document.createElement('canvas');
      return !!(window.WebGL2RenderingContext && c.getContext('webgl2'));
    } catch (e) { return false; }
  }
  function pickTier() {
    var mem = navigator.deviceMemory || 4;
    var cores = navigator.hardwareConcurrency || 4;
    var saveData = navigator.connection && navigator.connection.saveData;
    var mobile = /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
    if (saveData || mem <= 2 || cores <= 3) return 'low';
    if (mobile || mem <= 4 || cores <= 6) return 'mid';
    return 'high';
  }

  if (!hasWebGL2()) {
    loading.classList.add('hidden');
    fallback.classList.remove('hidden');
    ae('globe_webgl_unsupported', {});
    try { window.__aeCritical && window.__aeCritical('globe_webgl_unsupported', {}); } catch (e) {}
    return;
  }

  var TIER = pickTier();
  var THREE, renderer, scene, camera, raycaster;
  var earth, clouds, atmosphere, stars, moonMesh, moonGroup;
  var bordersLine, graticuleLine, analemmaLine, citiesPoints, citiesGroup;
  var issGroup, issMarker, issTrailLine, issFootprint, quakesGroup;
  var arcLine, centerMarker, highlightLine, measureGroup;
  var maxAniso = 4, hiResRequested = false;
  var measureMode = false, measurePts = [], userLoc = null;
  var sunVec = new THREE_placeholder(); // replaced after import
  function THREE_placeholder() { return { x: 0, y: 0, z: 0 }; }

  // ── State ───────────────────────────────────────────────────────────────
  var R = 1.0;
  var state = {
    clockMode: 'now',                       // 'now' | 'fixed'
    fixedDate: new Date(),
    style: 0,                               // 0 photoreal, 1 stylized
    autoRotate: !reduceMotion,
    layers: { night: true, clouds: true, atmosphere: true, borders: false, graticule: false,
              cities: false, twilight: false, analemma: false, moon: false, iss: false, quakes: false, nasa: false },
    cam: { theta: 2.4, phi: 1.3, radius: 2.7, vTheta: 0, vPhi: 0 },
    fly: null,                              // active fly-to tween
    dragging: false,
    arcFrom: null
  };
  var DIST_MIN = 1.25, DIST_MAX = 6.0;
  var cities = null, cityNames = null, countryFeats = null;
  var lastCenterName = '', centerTick = 0, idleResumeAt = 0;

  // ── Shaders ─────────────────────────────────────────────────────────────
  var EARTH_VERT = [
    'varying vec2 vUv; varying vec3 vNormalW; varying vec3 vWorldPos;',
    'varying vec3 vTangent; varying vec3 vBitangent;',
    'void main(){',
    '  vUv = uv;',
    '  vNormalW = normalize(mat3(modelMatrix) * normal);',
    '  vec3 up = abs(vNormalW.y) > 0.999 ? vec3(1.0,0.0,0.0) : vec3(0.0,1.0,0.0);',
    '  vTangent = normalize(cross(up, vNormalW));',
    '  vBitangent = cross(vNormalW, vTangent);',
    '  vec4 wp = modelMatrix * vec4(position,1.0);',
    '  vWorldPos = wp.xyz;',
    '  gl_Position = projectionMatrix * viewMatrix * wp;',
    '}'
  ].join('\n');

  var EARTH_FRAG = [
    'precision highp float;',
    'uniform sampler2D uDay,uNight,uSpec,uNormal,uClouds;',
    'uniform vec3 uSun,uCamera;',
    'uniform float uStylized,uShowNight,uShowClouds,uTwilight;',
    'varying vec2 vUv; varying vec3 vNormalW; varying vec3 vWorldPos;',
    'varying vec3 vTangent; varying vec3 vBitangent;',
    'vec3 s2l(vec3 c){ return pow(c, vec3(2.2)); }',
    'void main(){',
    '  vec3 nTex = texture2D(uNormal, vUv).xyz * 2.0 - 1.0;',
    '  nTex.xy *= 0.85;',
    '  vec3 N = normalize(vTangent*nTex.x + vBitangent*nTex.y + vNormalW*nTex.z);',
    '  vec3 Ngeo = normalize(vNormalW);',
    '  float c = dot(N, uSun);',
    '  float cGeo = dot(Ngeo, uSun);',
    '  float mixAmt = 1.0/(1.0+exp(-11.0*c));',
    '  vec3 dayCol = s2l(texture2D(uDay, vUv).rgb);',
    '  vec3 nightCol = s2l(texture2D(uNight, vUv).rgb);',
    '  float water = texture2D(uSpec, vUv).r;',
    '  vec3 viewDir = normalize(uCamera - vWorldPos);',
    '  // photoreal branch — modest day boost so Blue Marble reads vivid, faint terminator ambient',
    '  vec3 base = mix(nightCol*uShowNight*1.4, dayCol*1.28, mixAmt) + dayCol*0.03*(1.0-mixAmt);',
    '  vec3 refl = reflect(-uSun, N);',
    '  float spec = clamp(dot(refl, viewDir),0.0,1.0);',
    '  base += mixAmt * pow(spec, 18.0) * (0.45*water) * vec3(1.0,0.95,0.82);',
    '  float cl = pow(texture2D(uClouds, vUv).r, 1.5) * uShowClouds;',  // SSS white=cloud → soft shadow on land
    '  base *= (1.0 - 0.32*cl*mixAmt);',
    '  vec3 photo = base;',
    '  // stylized branch',
    '  float land = 1.0 - water;',
    '  float lf = smoothstep(0.35,0.55,land);',
    '  vec3 landCol = mix(vec3(0.10,0.40,0.30), vec3(0.52,0.74,0.45), clamp(dayCol.g*1.4,0.0,1.0));',
    '  vec3 oceanCol = vec3(0.035,0.12,0.32);',
    '  vec3 sb = mix(oceanCol, landCol, lf);',
    '  sb *= (0.16 + 0.95*clamp(cGeo,0.0,1.0));',
    '  sb += nightCol * (1.0-mixAmt) * uShowNight * 1.6 * lf;',
    '  vec3 styl = sb;',
    '  vec3 col = mix(photo, styl, uStylized);',
    '  // smooth twilight glow along the terminator: warm at the day/night line,',
    '  // fading through blue into the night, gone on the lit side (no hard bands)',
    '  if (uTwilight > 0.5) {',
    '    float alt = cGeo;',                                   // ~ sin(sun altitude); 0 at terminator
    '    float zone = smoothstep(-0.31, 0.0, alt);',           // 0 deep night -> 1 at terminator (-18deg..0)
    '    float dayFade = 1.0 - smoothstep(0.0, 0.10, alt);',   // kill on the lit side
    '    float glow = pow(zone, 2.0) * dayFade;',
    '    vec3 twCol = mix(vec3(0.04,0.06,0.16), vec3(0.32,0.15,0.05), pow(zone, 3.0));', // blue -> warm
    '    col += twCol * glow;',
    '  }',
    '  gl_FragColor = vec4(col, 1.0);',
    '}'
  ].join('\n');

  var CLOUD_FRAG = [
    'precision highp float;',
    'uniform sampler2D uClouds; uniform vec3 uSun; uniform float uShow;',
    'varying vec2 vUv; varying vec3 vNormalW;',
    'void main(){',
    '  float c = texture2D(uClouds, vUv).r;',           // SSS clouds: white=cloud, black=clear sky
    '  float a = pow(clamp(c,0.0,1.0), 1.5) * uShow;',   // soft density curve, thin haze stays subtle
    '  float lit = clamp(dot(normalize(vNormalW), uSun),0.0,1.0);',
    '  vec3 col = vec3(1.0)*(0.08+0.95*lit);',
    '  gl_FragColor = vec4(col, a*(0.45+0.5*lit));',
    '}'
  ].join('\n');
  var CLOUD_VERT = [
    'varying vec2 vUv; varying vec3 vNormalW;',
    'void main(){ vUv=uv; vNormalW=normalize(mat3(modelMatrix)*normal);',
    'gl_Position = projectionMatrix * modelViewMatrix * vec4(position,1.0); }'
  ].join('\n');

  var ATM_VERT = [
    'varying vec3 vNormalW; varying vec3 vWorldPos;',
    'void main(){ vNormalW=normalize(mat3(modelMatrix)*normal);',
    'vec4 wp=modelMatrix*vec4(position,1.0); vWorldPos=wp.xyz;',
    'gl_Position=projectionMatrix*viewMatrix*wp; }'
  ].join('\n');
  var ATM_FRAG = [
    'precision highp float;',
    'uniform vec3 uSun,uCamera,uTint; uniform float uIntensity;',
    'varying vec3 vNormalW; varying vec3 vWorldPos;',
    'void main(){',
    '  vec3 nrm = normalize(vWorldPos);',
    '  vec3 viewDir = normalize(uCamera - vWorldPos);',
    '  float fres = pow(1.0 - abs(dot(nrm, viewDir)), 2.6);',
    '  float lit = clamp(dot(nrm, uSun)*0.5+0.62, 0.0, 1.0);',
    '  float i = fres * uIntensity * lit;',
    '  gl_FragColor = vec4(uTint * i, i);',
    '}'
  ].join('\n');

  var LAMBERT_VERT = [
    'varying vec2 vUv; varying vec3 vNormalW;',
    'void main(){ vUv=uv; vNormalW=normalize(mat3(modelMatrix)*normal);',
    'gl_Position=projectionMatrix*modelViewMatrix*vec4(position,1.0); }'
  ].join('\n');
  var MOON_FRAG = [
    'precision highp float;',
    'uniform sampler2D uMap; uniform vec3 uSun;',
    'varying vec2 vUv; varying vec3 vNormalW;',
    'void main(){',
    '  float lit = clamp(dot(normalize(vNormalW), uSun)*1.1+0.04, 0.0, 1.0);',
    '  vec3 col = pow(texture2D(uMap,vUv).rgb, vec3(2.2)) * (0.03 + 1.15*lit);',
    '  gl_FragColor = vec4(col, 1.0);',
    '}'
  ].join('\n');

  // ── Boot: import three, build, run ──────────────────────────────────────
  function tryImport(primary, fb) {
    return import(primary).catch(function (e) { return (fb && fb !== primary) ? import(fb) : Promise.reject(e); });
  }
  loadingText(tt('engine_loading', tt('loading')));
  function loadingText(s) { var el = $('glbLoadingText'); if (el) el.textContent = s; }

  tryImport(CFG.three.primary, CFG.three.fallback).then(function (mod) {
    THREE = mod;
    sunVec = new THREE.Vector3(1, 0, 0);
    build();
  }).catch(function (e) {
    loading.classList.add('hidden');
    fallback.classList.remove('hidden');
    ae('globe_engine_failed', { msg: String(e && e.message || e) });
  });

  function vec(latlon, r) { var v = A.latLonToVec3(latlon.lat, latlon.lon, r); return new THREE.Vector3(v.x, v.y, v.z); }
  function vecLL(lat, lon, r) { var v = A.latLonToVec3(lat, lon, r); return new THREE.Vector3(v.x, v.y, v.z); }

  function build() {
    renderer = new THREE.WebGLRenderer({ canvas: canvas, antialias: TIER !== 'low', alpha: false, powerPreference: 'high-performance' });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, TIER === 'low' ? 1.25 : 2));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.NoToneMapping;
    scene = new THREE.Scene();
    camera = new THREE.PerspectiveCamera(42, 1, 0.01, 100);
    raycaster = new THREE.Raycaster();

    maxAniso = Math.min(renderer.capabilities.getMaxAnisotropy(), TIER === 'low' ? 2 : 8);
    var loader = new THREE.TextureLoader();
    function tex(url) {
      return new Promise(function (res) {
        loader.load(url, function (t) {
          t.colorSpace = THREE.NoColorSpace; t.anisotropy = maxAniso;
          t.wrapS = THREE.RepeatWrapping; res(t);
        }, undefined, function () { res(null); });
      });
    }

    // Starfield (procedural points) + slight GMST orientation
    buildStars();

    Promise.all([tex(CFG.tex.day), tex(CFG.tex.night), tex(CFG.tex.specular), tex(CFG.tex.normal), tex(CFG.tex.clouds)])
      .then(function (m) {
        var day = m[0], night = m[1], spec = m[2], norm = m[3], cloud = m[4];
        var seg = TIER === 'low' ? 48 : 64;
        // Earth
        var eMat = new THREE.ShaderMaterial({
          uniforms: {
            uDay: { value: day }, uNight: { value: night }, uSpec: { value: spec },
            uNormal: { value: norm }, uClouds: { value: cloud },
            uSun: { value: sunVec }, uCamera: { value: camera.position },
            uStylized: { value: 0 }, uShowNight: { value: 1 }, uShowClouds: { value: 1 }, uTwilight: { value: 0 }
          },
          vertexShader: EARTH_VERT, fragmentShader: EARTH_FRAG
        });
        earth = new THREE.Mesh(new THREE.SphereGeometry(R, seg, seg), eMat);
        scene.add(earth);

        // Clouds
        var cMat = new THREE.ShaderMaterial({
          uniforms: { uClouds: { value: cloud }, uSun: { value: sunVec }, uShow: { value: 1 } },
          vertexShader: CLOUD_VERT, fragmentShader: CLOUD_FRAG,
          transparent: true, depthWrite: false
        });
        clouds = new THREE.Mesh(new THREE.SphereGeometry(R * 1.006, seg, seg), cMat);
        scene.add(clouds);

        // Atmosphere shell (BackSide additive)
        var aMat = new THREE.ShaderMaterial({
          uniforms: { uSun: { value: sunVec }, uCamera: { value: camera.position }, uTint: { value: new THREE.Color(0.40, 0.62, 1.0) }, uIntensity: { value: 1.0 } },
          vertexShader: ATM_VERT, fragmentShader: ATM_FRAG,
          side: THREE.BackSide, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending
        });
        atmosphere = new THREE.Mesh(new THREE.SphereGeometry(R * 1.16, 48, 48), aMat);
        scene.add(atmosphere);

        finishBoot();
      });

    // Borders / cities load in parallel (non-blocking)
    loadGeo();
  }

  function buildStars() {
    var n = TIER === 'low' ? 1400 : 3200;
    var pos = new Float32Array(n * 3), col = new Float32Array(n * 3);
    for (var i = 0; i < n; i++) {
      var u = Math.random() * 2 - 1, th = Math.random() * Math.PI * 2, s = Math.sqrt(1 - u * u), r = 46 + Math.random() * 6;
      pos[i * 3] = r * s * Math.cos(th); pos[i * 3 + 1] = r * u; pos[i * 3 + 2] = r * s * Math.sin(th);
      var w = 0.6 + Math.random() * 0.4, tint = Math.random();
      col[i * 3] = w * (tint < 0.15 ? 0.8 : 1.0); col[i * 3 + 1] = w; col[i * 3 + 2] = w * (tint > 0.85 ? 1.0 : 0.92 + Math.random() * 0.08);
    }
    var g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    g.setAttribute('color', new THREE.BufferAttribute(col, 3));
    stars = new THREE.Points(g, new THREE.PointsMaterial({ size: 0.16, sizeAttenuation: true, vertexColors: true, transparent: true, opacity: 0.95, depthWrite: false }));
    stars.rotation.y = A.gmstDeg(getSimDate()) * A.D2R;
    scene.add(stars);
  }

  function finishBoot() {
    sizeRenderer();
    updateSun();
    applyCamera();
    applyPermalinkOrTimezone();
    loading.classList.add('hidden');
    setupUI();
    startLoop();
    ae('globe_ready', { tier: TIER });
    setQualityLabel();
    announce(tt('canvas_aria', 'Interactive 3D globe of Earth'));
  }

  // ── Progressive sharp textures (HIGH tier, on zoom-in) ──────────────────
  // Swaps the 2k day/night maps for 8k once the user zooms in, so the surface
  // stays crisp up close. RU loads from Beget (self-host), intl from the SSS CDN.
  function loadHiRes() {
    if (hiResRequested || TIER !== 'high' || !earth || !CFG.tex.day_hi) return;
    hiResRequested = true;
    var loader = new THREE.TextureLoader(); loader.setCrossOrigin('anonymous');
    function swap(url, uni) {
      loader.load(url, function (t) {
        t.colorSpace = THREE.NoColorSpace; t.anisotropy = maxAniso; t.wrapS = THREE.RepeatWrapping;
        if (earth && earth.material.uniforms[uni]) {
          var old = earth.material.uniforms[uni].value;
          earth.material.uniforms[uni].value = t;
          if (old && old.dispose) old.dispose();
        }
      }, undefined, function () { /* keep 2k on failure */ });
    }
    swap(CFG.tex.day_hi, 'uDay'); swap(CFG.tex.night_hi, 'uNight');
    ae('globe_layer_toggle', { layer: 'hires', on: true });
  }

  // ── Recent NASA imagery ("real clouds right now") ───────────────────────
  // Composite the last 3 days of VIIRS true-colour over the Blue Marble base
  // (each day has orbit gaps; 3 days fill the globe, the base fills any residue)
  // → a gap-free equirect texture with the actual recent cloud cover.
  var recentTex = null, savedDayTex = null, nasaLoading = false;
  function loadImg(url) {
    return new Promise(function (res, rej) {
      var img = new Image(); img.crossOrigin = 'anonymous';
      img.onload = function () { res(img); }; img.onerror = function () { rej(new Error(url)); };
      img.src = url;
    });
  }
  function loadRecentImagery() {
    if (recentTex) return Promise.resolve(recentTex);
    var urls = [CFG.tex.day, CFG.sat[2], CFG.sat[1], CFG.sat[0]];   // base, then oldest→newest
    return Promise.all(urls.map(loadImg)).then(function (imgs) {
      var w = 1024, h = 512, cv = document.createElement('canvas'); cv.width = w; cv.height = h;
      var ctx = cv.getContext('2d'); ctx.drawImage(imgs[0], 0, 0, w, h);
      var base = ctx.getImageData(0, 0, w, h), bd = base.data;
      for (var k = 1; k < imgs.length; k++) {
        var tc = document.createElement('canvas'); tc.width = w; tc.height = h;
        var tx = tc.getContext('2d'); tx.drawImage(imgs[k], 0, 0, w, h);
        var top = tx.getImageData(0, 0, w, h).data;
        for (var i = 0; i < bd.length; i += 4) {
          if (top[i] + top[i + 1] + top[i + 2] > 24) { bd[i] = top[i]; bd[i + 1] = top[i + 1]; bd[i + 2] = top[i + 2]; }
        }
      }
      ctx.putImageData(base, 0, 0);
      recentTex = new THREE.CanvasTexture(cv);
      recentTex.colorSpace = THREE.NoColorSpace; recentTex.wrapS = THREE.RepeatWrapping; recentTex.anisotropy = maxAniso;
      return recentTex;
    });
  }
  function toggleNasa(on) {
    if (!earth) return;
    if (on) {
      if (nasaLoading) return;
      nasaLoading = true; liveBox.classList.add('show'); setLive(tt('nasa_loading', 'Loading recent imagery…'));
      loadRecentImagery().then(function (tex) {
        nasaLoading = false;
        if (!state.layers.nasa) return;                          // toggled off while loading
        savedDayTex = earth.material.uniforms.uDay.value;
        earth.material.uniforms.uDay.value = tex;
        earth.material.uniforms.uShowClouds.value = 0;           // clouds are baked into the imagery
        if (clouds) clouds.visible = false;
        hiResRequested = true;                                   // keep the 8k swap from clobbering it
        if (!state.layers.iss && !state.layers.quakes) liveBox.classList.remove('show');
        ae('globe_layer_toggle', { layer: 'nasa', on: true });
      }).catch(function () { nasaLoading = false; setLive(tt('nasa_loading', '') + ' ✗'); });
    } else {
      if (savedDayTex) { earth.material.uniforms.uDay.value = savedDayTex; savedDayTex = null; }
      earth.material.uniforms.uShowClouds.value = state.layers.clouds ? 1 : 0;
      if (clouds) clouds.visible = state.layers.clouds && !state.style;
      if (!state.layers.iss && !state.layers.quakes) liveBox.classList.remove('show');
    }
  }

  // ── Geo data ────────────────────────────────────────────────────────────
  function loadGeo() {
    fetch(CFG.geo.borders_low).then(function (r) { return r.json(); }).then(function (topo) {
      try { countryFeats = A.topoCountries(topo, 'countries'); } catch (e) { countryFeats = null; }
    }).catch(function () {});
  }
  function ensureCities() {
    if (cities) return Promise.resolve();
    return Promise.all([
      fetch(CFG.geo.cities).then(function (r) { return r.json(); }),
      fetch(CFG.geo.city_names).then(function (r) { return r.json(); }).catch(function () { return null; })
    ]).then(function (a) { cities = a[0]; cityNames = a[1]; });
  }
  function ensureBordersHigh() {
    return fetch(CFG.geo.borders_high).then(function (r) { return r.json(); }).then(function (topo) { return A.topoCountries(topo, 'countries'); });
  }

  // ── Time / sun ──────────────────────────────────────────────────────────
  function getSimDate() {
    if (state.clockMode === 'now') return new Date();
    return new Date(state.fixedDate.getTime());
  }
  function updateSun() {
    var d = getSimDate();
    var ss = A.subsolarPoint(d);
    var v = A.latLonToVec3(ss.lat, ss.lon, 1);
    sunVec.set(v.x, v.y, v.z).normalize();
    if (stars) stars.rotation.y = A.gmstDeg(d) * A.D2R;
    state._subsolar = ss;
  }

  // ── Render loop ─────────────────────────────────────────────────────────
  var running = false, rafId = 0, lastT = 0, fpsAcc = 0, fpsN = 0, fpsLow = 0, cloudRot = 0;
  function startLoop() { if (running) return; running = true; lastT = performance.now(); rafId = requestAnimationFrame(loop); }
  function stopLoop() { running = false; if (rafId) cancelAnimationFrame(rafId); rafId = 0; }

  function loop(now) {
    if (!running) return;
    rafId = requestAnimationFrame(loop);
    var dt = Math.min((now - lastT) / 1000, 0.05); lastT = now;

    // fly-to tween
    if (state.fly) tickFly(now);

    // inertia + auto-rotate
    var cam = state.cam;
    if (!state.dragging && !state.fly) {
      cam.theta += cam.vTheta; cam.phi += cam.vPhi;
      cam.vTheta *= 0.92; cam.vPhi *= 0.92;
      if (Math.abs(cam.vTheta) < 1e-5) cam.vTheta = 0;
      if (Math.abs(cam.vPhi) < 1e-5) cam.vPhi = 0;
      if (state.autoRotate && now > idleResumeAt && cam.vTheta === 0) cam.theta += 0.0009 * (60 * dt);
    }
    cam.phi = A.clamp(cam.phi, 0.12, Math.PI - 0.12);
    applyCamera();
    if (!hiResRequested && state.cam.radius < 2.05) loadHiRes();

    if (state.clockMode === 'now') updateSun();
    if (moonGroup && moonGroup.visible && state.clockMode === 'now') updateMoon();
    if (clouds && !reduceMotion) { cloudRot += dt * 0.0011; clouds.rotation.y = cloudRot; }

    // periodic center readout (every ~280ms)
    centerTick += dt * 1000;
    if (centerTick > 280) { centerTick = 0; updateCenterReadout(); updateLabels(); }

    // pulse quakes
    if (state.layers.quakes && quakesGroup) pulseQuakes(now);

    renderer.render(scene, camera);

    // FPS watchdog
    fpsAcc += dt; fpsN++;
    if (fpsAcc >= 1) {
      var fps = fpsN / fpsAcc; fpsAcc = 0; fpsN = 0;
      if (fps < 32) { fpsLow++; if (fpsLow >= 3) degrade(); } else fpsLow = 0;
    }
  }

  var degraded = 0;
  function degrade() {
    fpsLow = 0;
    if (degraded === 0) { renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.25)); }
    else if (degraded === 1 && atmosphere) { atmosphere.material.uniforms.uIntensity.value *= 0.6; }
    else if (degraded === 2 && state.layers.clouds) { setLayer('clouds', false); syncChips(); }
    else return;
    degraded++;
    ae('globe_quality_degraded', { step: degraded });
    setQualityLabel();
  }

  function applyCamera() {
    var c = state.cam, r = c.radius, sp = Math.sin(c.phi);
    camera.position.set(r * sp * Math.sin(c.theta), r * Math.cos(c.phi), r * sp * Math.cos(c.theta));
    camera.lookAt(0, 0, 0);
    if (earth) earth.material.uniforms.uCamera.value = camera.position;
    if (atmosphere) atmosphere.material.uniforms.uCamera.value = camera.position;
  }

  function sizeRenderer() {
    var w = stage.clientWidth, h = stage.clientHeight;
    if (!w || !h) return;
    renderer.setSize(w, h, false);
    camera.aspect = w / h; camera.updateProjectionMatrix();
  }

  // ── Center-of-view readout + country detection + aria-live ──────────────
  function screenCenterLatLon() {
    raycaster.setFromCamera(new THREE.Vector2(0, 0), camera);
    var hit = raycaster.intersectObject(earth, false);
    if (!hit.length) return null;
    var p = hit[0].point;
    return A.vec3ToLatLon(p.x, p.y, p.z);
  }
  function localizedCountry(feat) {
    if (!feat) return null;
    var a2 = A.num2alpha2(+feat.id);
    if (a2 && window.Intl && Intl.DisplayNames) {
      try { var n = new Intl.DisplayNames([CFG.locale], { type: 'region' }).of(a2); if (n) return n; } catch (e) {}
    }
    return feat.name || null;
  }
  function updateCenterReadout() {
    if (!earth || !readout) return;
    var ll = screenCenterLatLon();
    var d = getSimDate(), ss = state._subsolar || A.subsolarPoint(d);
    var html = '';
    if (ll) {
      var feat = countryFeats ? A.countryAt(countryFeats, ll.lon, ll.lat) : null;
      var name = localizedCountry(feat) || tt('ocean', 'Open ocean');
      var coords = fmtLat(ll.lat) + ', ' + fmtLon(ll.lon);
      // local solar time at centered point
      var solarLocal = localSolarTimeStr(ll.lon, d, ss);
      html += '<div><b>' + esc(name) + '</b></div>';
      html += '<div class="glb-ro-sub">' + esc(coords) + '</div>';
      html += '<div class="glb-ro-sub">' + tt('local_time', 'Local solar time') + ': ' + esc(solarLocal) + '</div>';
      var srs = A.sunRiseSetLocal(ll.lat, d);
      var srsStr = srs.state === 'normal' ? ('🌅 ' + minToHHMM(srs.rise) + ' · 🌇 ' + minToHHMM(srs.set))
        : (srs.state === 'polar_day' ? '☀ 24h' : '🌑 0h');
      html += '<div class="glb-ro-sub">' + srsStr + '</div>';
      if (name !== lastCenterName && feat) { announce(tt('centered_on', 'Centered on') + ': ' + name); }
      lastCenterName = name;
    }
    html += '<div class="glb-ro-sub">' + tt('subsolar', 'Sun overhead') + ': ' + fmtLat(ss.lat) + ', ' + fmtLon(ss.lon) + '</div>';
    if (state.layers.moon && state._moon) {
      var mp = state._moon, pk = A.moonPhaseKey(mp.elong);
      html += '<div class="glb-ro-sub">🌙 ' + esc(tt(pk, pk)) + ' · ' + tt('moon_illum', 'Illuminated') + ' ' + Math.round(mp.illum * 100) + '%</div>';
    }
    html += '<div class="glb-ro-sub">' + fmtDateUTC(d) + '</div>';
    readout.innerHTML = html;
  }
  function localSolarTimeStr(lon, date, ss) {
    // local apparent solar time ≈ UTC + lon/15 (+ EoT already in subsolar); show clock
    var utcMin = date.getUTCHours() * 60 + date.getUTCMinutes();
    var t = utcMin + lon * 4; // 4 min per degree
    t = ((t % 1440) + 1440) % 1440;
    var hh = Math.floor(t / 60), mm = Math.floor(t % 60);
    return (hh < 10 ? '0' : '') + hh + ':' + (mm < 10 ? '0' : '') + mm;
  }
  function minToHHMM(m) { m = ((m % 1440) + 1440) % 1440; var hh = Math.floor(m / 60), mm = Math.floor(m % 60); return (hh < 10 ? '0' : '') + hh + ':' + (mm < 10 ? '0' : '') + mm; }
  function fmtLat(v) { return Math.abs(v).toFixed(1) + '°' + (v >= 0 ? 'N' : 'S'); }
  function fmtLon(v) { return Math.abs(v).toFixed(1) + '°' + (v >= 0 ? 'E' : 'W'); }
  function fmtDateUTC(d) {
    try { return new Intl.DateTimeFormat(CFG.locale, { dateStyle: 'medium', timeStyle: 'short', timeZone: 'UTC' }).format(d) + ' UTC'; }
    catch (e) { return d.toUTCString(); }
  }
  function esc(s) { return String(s).replace(/[&<>"]/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]; }); }

  // ── Camera fly-to ───────────────────────────────────────────────────────
  function targetThetaPhi(lat, lon) {
    var v = A.latLonToVec3(lat, lon, 1);
    var phi = Math.acos(A.clamp(v.y, -1, 1));
    var theta = Math.atan2(v.x, v.z);
    return { theta: theta, phi: phi };
  }
  function flyTo(lat, lon, label, zoomIn) {
    var tp = targetThetaPhi(lat, lon);
    var c = state.cam;
    // shortest theta path
    var dTheta = tp.theta - c.theta;
    while (dTheta > Math.PI) dTheta -= 2 * Math.PI;
    while (dTheta < -Math.PI) dTheta += 2 * Math.PI;
    var targetR = zoomIn ? Math.max(DIST_MIN + 0.35, Math.min(c.radius, 1.9)) : c.radius;
    if (reduceMotion) {
      c.theta = tp.theta; c.phi = A.clamp(tp.phi, 0.12, Math.PI - 0.12); c.radius = targetR;
      c.vTheta = c.vPhi = 0; state.fly = null; applyCamera();
    } else {
      state.fly = { t0: performance.now(), dur: 900, fromT: c.theta, dT: dTheta, fromP: c.phi, toP: A.clamp(tp.phi, 0.12, Math.PI - 0.12), fromR: c.radius, toR: targetR };
    }
    idleResumeAt = performance.now() + 4000;
    if (label) announce(tt('fly_to', 'Flying to') + ' ' + label);
  }
  function tickFly(now) {
    var f = state.fly, k = A.clamp((now - f.t0) / f.dur, 0, 1);
    var e = k < 0.5 ? 2 * k * k : 1 - Math.pow(-2 * k + 2, 2) / 2; // easeInOutQuad
    var c = state.cam;
    c.theta = f.fromT + f.dT * e; c.phi = f.fromP + (f.toP - f.fromP) * e; c.radius = f.fromR + (f.toR - f.fromR) * e;
    c.vTheta = c.vPhi = 0;
    if (k >= 1) state.fly = null;
  }

  // ── Layers (borders, graticule, cities, analemma, moon, iss, quakes) ────
  function lineFromRings(rings, r, color, op) {
    var pts = [];
    for (var i = 0; i < rings.length; i++) {
      var ring = rings[i];
      for (var j = 0; j < ring.length - 1; j++) {
        var a = A.latLonToVec3(ring[j][1], ring[j][0], r), b = A.latLonToVec3(ring[j + 1][1], ring[j + 1][0], r);
        pts.push(a.x, a.y, a.z, b.x, b.y, b.z);
      }
    }
    var g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(pts, 3));
    return new THREE.LineSegments(g, new THREE.LineBasicMaterial({ color: color, transparent: true, opacity: op, depthWrite: false }));
  }
  function highlightCountry(feat) {
    clearHighlight();
    if (!feat || !feat.polygons) return;
    var rings = [];
    for (var p = 0; p < feat.polygons.length; p++) for (var r = 0; r < feat.polygons[p].length; r++) rings.push(feat.polygons[p][r]);
    highlightLine = lineFromRings(rings, R * 1.004, 0x9af2ff, 1.0);
    scene.add(highlightLine);
  }
  function clearHighlight() { if (highlightLine) { scene.remove(highlightLine); highlightLine.geometry.dispose(); highlightLine = null; } }

  function buildBorders() {
    if (bordersLine) { bordersLine.visible = true; return; }
    var build = function (feats) {
      var rings = [];
      for (var i = 0; i < feats.length; i++) for (var p = 0; p < feats[i].polygons.length; p++) {
        var poly = feats[i].polygons[p]; for (var r = 0; r < poly.length; r++) rings.push(poly[r]);
      }
      bordersLine = lineFromRings(rings, R * 1.0015, 0x6fb6ff, 0.55);
      scene.add(bordersLine);
    };
    if (TIER === 'high') ensureBordersHigh().then(build).catch(function () { if (countryFeats) build(countryFeats); });
    else if (countryFeats) build(countryFeats);
    else loadGeo(); // retry next toggle
  }
  function buildGraticule() {
    if (graticuleLine) { graticuleLine.visible = true; return; }
    var rings = [];
    for (var lat = -60; lat <= 60; lat += 30) { var r = []; for (var lo = -180; lo <= 180; lo += 5) r.push([lo, lat]); rings.push(r); }
    for (var lon = -150; lon <= 180; lon += 30) { var rr = []; for (var la = -85; la <= 85; la += 5) rr.push([lon, la]); rings.push(rr); }
    // equator brighter
    var eq = []; for (var e = -180; e <= 180; e += 5) eq.push([e, 0]);
    graticuleLine = new THREE.Group();
    graticuleLine.add(lineFromRings(rings, R * 1.0012, 0x4a6a9a, 0.32));
    graticuleLine.add(lineFromRings([eq], R * 1.0014, 0x7fd0ff, 0.5));
    scene.add(graticuleLine);
  }
  function buildAnalemma() {
    if (analemmaLine) { analemmaLine.visible = true; return; }
    // The subsolar point at a fixed UTC noon traces this figure-8 over a year
    // (latitude = solar declination ±23.44°, longitude wobble = equation of time).
    analemmaLine = new THREE.Group();
    var pts = [], yr = getSimDate().getUTCFullYear();
    for (var doy = 0; doy <= 366; doy += 2) {
      var dd = new Date(Date.UTC(yr, 0, 1 + doy, 12, 0, 0));
      var ss = A.subsolarPoint(dd), v = A.latLonToVec3(ss.lat, ss.lon, R * 1.015);
      pts.push(v.x, v.y, v.z);
    }
    var g = new THREE.BufferGeometry(); g.setAttribute('position', new THREE.Float32BufferAttribute(pts, 3));
    analemmaLine.add(new THREE.Line(g, new THREE.LineBasicMaterial({ color: 0xffd27a, transparent: true, opacity: 0.95, depthWrite: false })));
    // solstice (gold) + equinox (blue) markers so it reads as the Sun's yearly path
    var marks = [[79, 0x7fd0ff], [172, 0xffcf6a], [265, 0x7fd0ff], [355, 0xffcf6a]];
    for (var i = 0; i < marks.length; i++) {
      var d2 = new Date(Date.UTC(yr, 0, 1 + marks[i][0], 12, 0, 0)), s2 = A.subsolarPoint(d2);
      var p = A.latLonToVec3(s2.lat, s2.lon, R * 1.015);
      var dot = new THREE.Mesh(new THREE.SphereGeometry(0.013, 12, 12), new THREE.MeshBasicMaterial({ color: marks[i][1] }));
      dot.position.set(p.x, p.y, p.z); analemmaLine.add(dot);
    }
    scene.add(analemmaLine);
  }
  function buildMoon() {
    if (moonGroup) { moonGroup.visible = true; updateMoon(); return; }
    moonGroup = new THREE.Group();
    var loader = new THREE.TextureLoader();
    loader.load(CFG.tex.moon, function (mt) {
      mt.colorSpace = THREE.NoColorSpace;
      var mat = new THREE.ShaderMaterial({ uniforms: { uMap: { value: mt }, uSun: { value: sunVec } }, vertexShader: LAMBERT_VERT, fragmentShader: MOON_FRAG });
      moonMesh = new THREE.Mesh(new THREE.SphereGeometry(0.27, 32, 32), mat);
      moonGroup.add(moonMesh); scene.add(moonGroup); updateMoon();
    });
  }
  function updateMoon() {
    if (!moonMesh) return;
    var d = getSimDate(), mp = A.moon(d);
    var dir = A.latLonToVec3(mp.subLat, mp.subLon, 1);
    moonMesh.position.set(dir.x, dir.y, dir.z).multiplyScalar(4.2);
    moonMesh.lookAt(0, 0, 0);
    state._moon = mp;
  }

  // ── Cities (soft glowing points + DOM labels) ───────────────────────────
  // City data is the shared Timbrica module (/data/cities — GeoNames, localized).
  var labelEls = [], cityDotTex = null;
  function getCityDotTex() {
    if (cityDotTex) return cityDotTex;
    var c = document.createElement('canvas'); c.width = c.height = 64;
    var x = c.getContext('2d');
    var g = x.createRadialGradient(32, 32, 0, 32, 32, 32);
    g.addColorStop(0.0, 'rgba(255,251,236,1)');
    g.addColorStop(0.22, 'rgba(255,226,150,0.95)');
    g.addColorStop(0.5, 'rgba(255,182,92,0.34)');
    g.addColorStop(1.0, 'rgba(255,172,82,0)');
    x.fillStyle = g; x.beginPath(); x.arc(32, 32, 32, 0, 6.2832); x.fill();
    cityDotTex = new THREE.CanvasTexture(c); cityDotTex.colorSpace = THREE.SRGBColorSpace;
    return cityDotTex;
  }
  function buildCities() {
    ensureCities().then(function () {
      if (!cities) return;
      if (!citiesPoints) {
        var top = Math.min(cities.length, TIER === 'low' ? 250 : 700);
        var pos = new Float32Array(top * 3);
        for (var i = 0; i < top; i++) {
          var c = cities[i], v = A.latLonToVec3(c[0], c[1], R * 1.004);
          pos[i * 3] = v.x; pos[i * 3 + 1] = v.y; pos[i * 3 + 2] = v.z;
        }
        var g = new THREE.BufferGeometry(); g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
        citiesPoints = new THREE.Points(g, new THREE.PointsMaterial({
          map: getCityDotTex(), size: 0.03, sizeAttenuation: true,
          transparent: true, opacity: 0.95, depthWrite: false, alphaTest: 0.01
        }));
        scene.add(citiesPoints);
        buildCityLabels();
      } else { citiesPoints.visible = true; labelEls.forEach(function (l) { l.el.style.display = ''; }); }
    });
  }
  function buildCityLabels() {
    var n = Math.min(cities.length, TIER === 'low' ? 30 : 70);
    for (var i = 0; i < n; i++) {
      var c = cities[i];
      var name = (cityNames && cityNames[i]) || c[4];
      var el = document.createElement('div');
      el.className = 'glb-city-label';
      el.textContent = name;
      el.style.cssText = 'position:absolute;transform:translate(-50%,-150%);font-size:12px;font-weight:600;color:#fff;text-shadow:0 0 4px #000,0 1px 2px #000;pointer-events:none;white-space:nowrap;z-index:2;will-change:transform;';
      stage.appendChild(el);
      var v = A.latLonToVec3(c[0], c[1], R * 1.01);
      labelEls.push({ el: el, v: new THREE.Vector3(v.x, v.y, v.z) });
    }
  }
  function updateLabels() {
    if (!labelEls.length || !state.layers.cities) return;
    var w = stage.clientWidth, h = stage.clientHeight, camPos = camera.position;
    var placed = [];
    for (var i = 0; i < labelEls.length; i++) {
      var L = labelEls[i], p = L.v;
      var front = p.clone().normalize().dot(camPos.clone().sub(p).normalize()) > 0.12;
      if (!front) { L.el.style.display = 'none'; continue; }
      var s = p.clone().project(camera);
      if (s.z > 1) { L.el.style.display = 'none'; continue; }
      var x = (s.x * 0.5 + 0.5) * w, y = (-s.y * 0.5 + 0.5) * h;
      var ok = true;
      for (var j = 0; j < placed.length; j++) { if (Math.abs(placed[j][0] - x) < 54 && Math.abs(placed[j][1] - y) < 16) { ok = false; break; } }
      if (!ok) { L.el.style.display = 'none'; continue; }
      placed.push([x, y]); L.el.style.display = ''; L.el.style.left = x + 'px'; L.el.style.top = y + 'px';
    }
  }

  // ── ISS (bundled TLE + client-side SGP4 via satellite.js) ───────────────
  // Reliable + zero per-frame network: fetch the TLE once (cached on our origin
  // from Celestrak), then propagate the position locally every second and draw
  // the real predicted orbit ground-track. Works for RU users behind blocks.
  var issTimer = 0, issSatLib = null, issSatrec = null, issGroundTrack = null;
  function loadSatLib() {
    if (issSatLib) return Promise.resolve(issSatLib);
    if (window.satellite) { issSatLib = window.satellite; return Promise.resolve(issSatLib); }
    return new Promise(function (res, rej) {
      function add(src, onerr) { var s = document.createElement('script'); s.src = src; s.onload = function () { issSatLib = window.satellite; res(issSatLib); }; s.onerror = onerr; document.head.appendChild(s); }
      add(CFG.satellite.primary, function () { add(CFG.satellite.fallback, function () { rej(new Error('satellite.js failed')); }); });
    });
  }
  function startISS() {
    if (!issGroup) { issGroup = new THREE.Group(); scene.add(issGroup); }
    issGroup.visible = true; liveBox.classList.add('show'); setLive(tt('iss_loading', 'Locating ISS…'));
    loadSatLib()
      .then(function () { return fetch(CFG.live.tle, { cache: 'no-store' }).then(function (r) { return r.json(); }); })
      .then(function (tle) {
        if (!tle || !tle.l1 || !tle.l2) throw new Error('no tle');
        issSatrec = issSatLib.twoline2satrec(tle.l1, tle.l2);
        buildGroundTrack(); refreshISSPasses(); updateISS();
        if (issTimer) clearInterval(issTimer);
        issTimer = setInterval(updateISS, 1000);
      })
      .catch(function () { setLive(tt('iss_offline', 'ISS position unavailable')); });
  }
  function stopISS() { if (issTimer) clearInterval(issTimer); issTimer = 0; if (issGroup) issGroup.visible = false; if (!state.layers.quakes) liveBox.classList.remove('show'); }
  function issGeo(date) {
    try {
      var pv = issSatLib.propagate(issSatrec, date);
      if (!pv || !pv.position) return null;
      var g = issSatLib.eciToGeodetic(pv.position, issSatLib.gstime(date)), v = pv.velocity;
      return { lat: issSatLib.degreesLat(g.latitude), lon: issSatLib.degreesLong(g.longitude), alt: g.height,
        vel: v ? Math.sqrt(v.x * v.x + v.y * v.y + v.z * v.z) * 3600 : 0 };
    } catch (e) { return null; }
  }
  function issRad(alt) { return R * (1 + Math.min(alt / 6371 * 1.5, 0.18)); }
  function updateISS() {
    if (!issSatrec || !issSatLib) return;
    var p = issGeo(new Date());
    if (!p || isNaN(p.lat)) { setLive(tt('iss_offline', 'ISS position unavailable')); return; }
    var v = A.latLonToVec3(p.lat, p.lon, issRad(p.alt));
    if (!issMarker) { issMarker = new THREE.Mesh(new THREE.SphereGeometry(0.024, 14, 14), new THREE.MeshBasicMaterial({ color: 0x7af0c0 })); issGroup.add(issMarker); }
    issMarker.position.set(v.x, v.y, v.z);
    var fr = A.footprintRadiusDeg(p.alt), circ = A.smallCircle(p.lat, p.lon, fr, 64), fp = [];
    for (var i = 0; i < circ.length; i++) { var q = A.latLonToVec3(circ[i].lat, circ[i].lon, R * 1.002); fp.push(q.x, q.y, q.z); }
    if (issFootprint) issGroup.remove(issFootprint);
    var fg = new THREE.BufferGeometry(); fg.setAttribute('position', new THREE.Float32BufferAttribute(fp, 3));
    issFootprint = new THREE.Line(fg, new THREE.LineBasicMaterial({ color: 0x7af0c0, transparent: true, opacity: 0.35 }));
    issGroup.add(issFootprint);
    var passLine = '';
    if (issNextPass) {
      var pt = ''; try { pt = new Intl.DateTimeFormat(CFG.locale, { hour: '2-digit', minute: '2-digit' }).format(issNextPass.start); } catch (e) {}
      passLine = '<br>🛰️ ' + pt + ' · ↑' + issNextPass.maxEl + '°';
    }
    setLive('<b>' + tt('iss_label', 'ISS') + '</b> · ' + fmtLat(p.lat) + ' ' + fmtLon(p.lon) + '<br>' +
      tt('iss_alt', 'Altitude') + ': ' + Math.round(p.alt) + ' km · ' + tt('iss_vel', 'Speed') + ': ' + fmtPop(Math.round(p.vel)) + ' km/h' + passLine);
  }
  function buildGroundTrack() {
    if (!issSatrec || !issSatLib) return;
    if (issGroundTrack) { issGroup.remove(issGroundTrack); issGroundTrack.geometry.dispose(); issGroundTrack = null; }
    var pts = [], now = Date.now();
    for (var m = -25; m <= 95; m += 1) {        // ~one full orbit (~93 min) around now
      var p = issGeo(new Date(now + m * 60000));
      if (!p || isNaN(p.lat)) continue;
      var v = A.latLonToVec3(p.lat, p.lon, R * 1.003);   // ground projection
      pts.push(v.x, v.y, v.z);
    }
    var g = new THREE.BufferGeometry(); g.setAttribute('position', new THREE.Float32BufferAttribute(pts, 3));
    issGroundTrack = new THREE.Line(g, new THREE.LineBasicMaterial({ color: 0x7af0c0, transparent: true, opacity: 0.4 }));
    issGroup.add(issGroundTrack);
  }
  // Next visible ISS passes over a ground point (elevation > 10°) from the TLE.
  var issNextPass = null;
  function computeISSPasses(lat, lon, maxPasses) {
    if (!issSatrec || !issSatLib || !issSatLib.ecfToLookAngles) return [];
    var obs = { latitude: lat * A.D2R, longitude: lon * A.D2R, height: 0.1 };
    var out = [], now = Date.now(), prevEl = -90, start = null, maxEl = 0;
    for (var s = 0; s < 24 * 3600 && out.length < (maxPasses || 1); s += 30) {
      var d = new Date(now + s * 1000), pv = issSatLib.propagate(issSatrec, d);
      if (!pv || !pv.position) continue;
      var ecf = issSatLib.eciToEcf(pv.position, issSatLib.gstime(d));
      var el = issSatLib.ecfToLookAngles(obs, ecf).elevation * A.R2D;
      if (el > 10 && prevEl <= 10) { start = d; maxEl = el; }
      else if (start && el > maxEl) maxEl = el;
      else if (el <= 10 && prevEl > 10 && start) { out.push({ start: start, maxEl: Math.round(maxEl) }); start = null; maxEl = 0; }
      prevEl = el;
    }
    return out;
  }
  function refreshISSPasses() {
    if (userLoc && issSatrec) { var p = computeISSPasses(userLoc.lat, userLoc.lon, 1); issNextPass = p[0] || null; }
    else issNextPass = null;
  }

  // ── Earthquakes ─────────────────────────────────────────────────────────
  var quakeTimer = 0, quakeMeshes = [];
  function startQuakes() {
    if (!quakesGroup) { quakesGroup = new THREE.Group(); scene.add(quakesGroup); }
    quakesGroup.visible = true; liveBox.classList.add('show');
    fetchQuakes(); quakeTimer = setInterval(fetchQuakes, 120000);
  }
  function stopQuakes() { if (quakeTimer) clearInterval(quakeTimer); quakeTimer = 0; if (quakesGroup) quakesGroup.visible = false; if (!state.layers.iss) liveBox.classList.remove('show'); }
  function fetchQuakes() {
    fetch(CFG.live.quakes, { cache: 'no-store' }).then(function (r) { return r.json(); }).then(function (d) {
      while (quakesGroup.children.length) quakesGroup.remove(quakesGroup.children[0]);
      quakeMeshes = [];
      var feats = (d.features || []).filter(function (f) { return f.geometry && f.properties && f.properties.mag >= 1; });
      feats.sort(function (a, b) { return b.properties.mag - a.properties.mag; });
      feats = feats.slice(0, 300);
      for (var i = 0; i < feats.length; i++) {
        var f = feats[i], co = f.geometry.coordinates, mag = f.properties.mag;
        var v = A.latLonToVec3(co[1], co[0], R * 1.004);
        var sz = 0.01 + mag * 0.006;
        var col = mag >= 5 ? 0xff4d4d : mag >= 3 ? 0xffa64d : 0xffe14d;
        var m = new THREE.Mesh(new THREE.SphereGeometry(sz, 10, 10), new THREE.MeshBasicMaterial({ color: col, transparent: true, opacity: 0.85 }));
        m.position.set(v.x, v.y, v.z);
        m.userData = { base: sz, mag: mag, lat: co[1], lon: co[0], depth: co[2], place: f.properties.place, time: f.properties.time };
        quakesGroup.add(m); quakeMeshes.push(m);
      }
      quakeCount = feats.length;
      if (state.layers.quakes) setLive('<b>' + tt('quakes_count', 'Earthquakes (24h)') + '</b>: ' + quakeCount);
    }).catch(function () { if (state.layers.quakes) setLive(tt('quakes_offline', 'Earthquake data unavailable')); });
  }
  function pulseQuakes(now) {
    var s = 1 + 0.25 * Math.sin(now * 0.004);
    for (var i = 0; i < quakeMeshes.length; i++) { var m = quakeMeshes[i]; m.scale.setScalar(m.userData.mag >= 4 ? s : 1); }
  }
  function setLive(html) { liveBox.innerHTML = html; }

  // ── Layer toggling ──────────────────────────────────────────────────────
  function setLayer(name, on) {
    state.layers[name] = on;
    switch (name) {
      case 'night': if (earth) earth.material.uniforms.uShowNight.value = on ? 1 : 0; break;
      case 'clouds': if (clouds) clouds.visible = on; if (earth) earth.material.uniforms.uShowClouds.value = on ? 1 : 0; break;
      case 'atmosphere': if (atmosphere) atmosphere.visible = on; break;
      case 'borders': on ? buildBorders() : (bordersLine && (bordersLine.visible = false)); break;
      case 'graticule': on ? buildGraticule() : (graticuleLine && (graticuleLine.visible = false)); break;
      case 'cities': on ? buildCities() : hideCities(); break;
      case 'analemma': on ? buildAnalemma() : (analemmaLine && (analemmaLine.visible = false)); break;
      case 'moon': on ? buildMoon() : (moonGroup && (moonGroup.visible = false)); break;
      case 'iss': on ? startISS() : stopISS(); break;
      case 'quakes': on ? startQuakes() : stopQuakes(); break;
      case 'nasa': toggleNasa(on); break;
      case 'twilight': if (earth) earth.material.uniforms.uTwilight.value = on ? 1 : 0; break;
    }
    ae('globe_layer_toggle', { layer: name, on: on });
  }
  function hideCities() { if (citiesPoints) citiesPoints.visible = false; labelEls.forEach(function (l) { l.el.style.display = 'none'; }); }

  function setStyle(styl) {
    state.style = styl;
    if (earth) earth.material.uniforms.uStylized.value = styl;
    if (clouds) clouds.visible = styl ? false : state.layers.clouds;
    if (atmosphere) atmosphere.material.uniforms.uIntensity.value = styl ? 1.7 : 1.0;
    ae('globe_style', { stylized: styl });
  }

  // ── Hover tooltip (nearest city) ────────────────────────────────────────
  function onHover(ev) {
    if (!earth || !cities) { hideTip(); return; }
    var rect = stage.getBoundingClientRect();
    var nx = ((ev.clientX - rect.left) / rect.width) * 2 - 1, ny = -((ev.clientY - rect.top) / rect.height) * 2 + 1;
    raycaster.setFromCamera(new THREE.Vector2(nx, ny), camera);
    var hit = raycaster.intersectObject(earth, false);
    if (!hit.length) { hideTip(); return; }
    var ll = A.vec3ToLatLon(hit[0].point.x, hit[0].point.y, hit[0].point.z);
    var best = null, bestD = 6;
    var lim = Math.min(cities.length, 1200);
    for (var i = 0; i < lim; i++) {
      var c = cities[i], dlat = c[0] - ll.lat, dlon = (c[1] - ll.lon);
      if (dlon > 180) dlon -= 360; else if (dlon < -180) dlon += 360;
      var dd = dlat * dlat + dlon * dlon * Math.pow(Math.cos(ll.lat * A.D2R), 2);
      if (dd < bestD) { bestD = dd; best = i; }
    }
    if (best == null || bestD > 4) { hideTip(); return; }
    var cc = cities[best], nm = (cityNames && cityNames[best]) || cc[4];
    var country = '';
    if (cc[2] && window.Intl && Intl.DisplayNames) { try { country = new Intl.DisplayNames([CFG.locale], { type: 'region' }).of(cc[2]) || cc[2]; } catch (e) { country = cc[2]; } }
    var px = ev.clientX - rect.left, py = ev.clientY - rect.top;
    tooltip.style.left = px + 'px'; tooltip.style.top = py + 'px';
    tooltip.innerHTML = esc(nm) + '<small>' + esc(country) + (cc[5] ? ' · ' + fmtPop(cc[5]) : '') + '</small>';
    tooltip.classList.remove('hidden');
  }
  function hideTip() { tooltip.classList.add('hidden'); }
  function fmtPop(p) { try { return new Intl.NumberFormat(CFG.locale).format(p); } catch (e) { return '' + p; } }

  // ── Click / tap on the globe: measure points, earthquake details ────────
  function onGlobeClick(clientX, clientY) {
    if (!earth) return;
    var rect = stage.getBoundingClientRect();
    var nx = ((clientX - rect.left) / rect.width) * 2 - 1, ny = -((clientY - rect.top) / rect.height) * 2 + 1;
    raycaster.setFromCamera(new THREE.Vector2(nx, ny), camera);
    var hit = raycaster.intersectObject(earth, false);
    if (!hit.length) return;
    var ll = A.vec3ToLatLon(hit[0].point.x, hit[0].point.y, hit[0].point.z);
    if (measureMode) { addMeasurePoint(ll.lat, ll.lon); return; }
    // nearest earthquake within a few degrees of the tap
    if (state.layers.quakes && quakeMeshes.length) {
      var best = null, bestD = 12;
      for (var i = 0; i < quakeMeshes.length; i++) {
        var qd = quakeMeshes[i].userData; if (qd.lat == null) continue;
        var dlat = qd.lat - ll.lat, dlon = qd.lon - ll.lon;
        if (dlon > 180) dlon -= 360; else if (dlon < -180) dlon += 360;
        var dd = dlat * dlat + dlon * dlon * Math.pow(Math.cos(ll.lat * A.D2R), 2);
        if (dd < bestD) { bestD = dd; best = qd; }
      }
      if (best && bestD < 9) { showQuakePopup(best); return; }
    }
  }
  var quakePopupTimer = 0, quakeCount = 0;
  function showQuakePopup(qd) {
    var when = '';
    try { when = new Intl.DateTimeFormat(CFG.locale, { dateStyle: 'short', timeStyle: 'short' }).format(new Date(qd.time)); } catch (e) {}
    liveBox.classList.add('show');
    setLive('<b>M ' + qd.mag.toFixed(1) + '</b> · ' + esc(qd.place || '') + '<br>' +
      (qd.depth != null ? Math.round(qd.depth) + ' km · ' : '') + esc(when));
    announce(tt('quakes_mag', 'Magnitude') + ' ' + qd.mag.toFixed(1) + ' · ' + (qd.place || ''));
    ae('globe_layer_toggle', { layer: 'quake_click', on: true });
    if (quakePopupTimer) clearTimeout(quakePopupTimer);
    quakePopupTimer = setTimeout(function () {
      if (state.layers.quakes) setLive('<b>' + tt('quakes_count', 'Earthquakes (24h)') + '</b>: ' + quakeCount);
    }, 7000);
  }

  // ── Distance measure (tap two points → great-circle arc + km) ────────────
  function addMeasurePoint(lat, lon) {
    if (!measureGroup) { measureGroup = new THREE.Group(); scene.add(measureGroup); }
    if (measurePts.length >= 2) clearMeasure();
    measurePts.push({ lat: lat, lon: lon });
    var v = A.latLonToVec3(lat, lon, R * 1.008);
    var dot = new THREE.Mesh(new THREE.SphereGeometry(0.014, 12, 12), new THREE.MeshBasicMaterial({ color: 0xff5a8a }));
    dot.position.set(v.x, v.y, v.z); measureGroup.add(dot);
    if (measurePts.length === 2) {
      var a = measurePts[0], b = measurePts[1];
      var gc = A.greatCirclePoints(a.lat, a.lon, b.lat, b.lon, 96), pos = [];
      for (var i = 0; i < gc.length; i++) { var p = A.latLonToVec3(gc[i].lat, gc[i].lon, R * 1.008); pos.push(p.x, p.y, p.z); }
      var g = new THREE.BufferGeometry(); g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
      measureGroup.add(new THREE.Line(g, new THREE.LineBasicMaterial({ color: 0xff5a8a, transparent: true, opacity: 0.9 })));
      var km = A.haversineKm(a.lat, a.lon, b.lat, b.lon);
      var txt = fmtPop(Math.round(km)) + ' ' + tt('km', 'km') + ' · ' + fmtPop(Math.round(km * 0.621371)) + ' mi';
      liveBox.classList.add('show'); setLive('<b>' + tt('distance', 'Distance') + '</b>: ' + txt);
      announce(tt('distance', 'Distance') + ': ' + txt);
      ae('globe_layer_toggle', { layer: 'measure', on: true });
    }
  }
  function clearMeasure() {
    if (measureGroup) { while (measureGroup.children.length) { var c = measureGroup.children[0]; measureGroup.remove(c); if (c.geometry) c.geometry.dispose(); } }
    measurePts = [];
  }
  function setMeasureMode(on) {
    measureMode = on;
    var b = $('glbMeasure'); if (b) { b.classList.toggle('active', on); b.setAttribute('aria-pressed', on ? 'true' : 'false'); }
    stage.style.cursor = on ? 'crosshair' : '';
    if (on) { clearMeasure(); liveBox.classList.add('show'); setLive(tt('arc_hint', 'Tap two places to measure the distance')); }
    else { clearMeasure(); if (!state.layers.iss && !state.layers.quakes) liveBox.classList.remove('show'); }
  }

  // ── Search (countries + cities) ─────────────────────────────────────────
  function regionName(a2) { try { if (window.Intl && Intl.DisplayNames) return new Intl.DisplayNames([CFG.locale], { type: 'region' }).of(a2) || a2; } catch (e) {} return a2; }
  function countryCentroid(feat) {
    var best = null, bestArea = -1;
    for (var p = 0; p < feat.polygons.length; p++) {
      var ring = feat.polygons[p][0]; if (!ring || ring.length < 3) continue;
      var minx = 1e9, maxx = -1e9, miny = 1e9, maxy = -1e9;
      for (var i = 0; i < ring.length; i++) { var x = ring[i][0], y = ring[i][1]; if (x < minx) minx = x; if (x > maxx) maxx = x; if (y < miny) miny = y; if (y > maxy) maxy = y; }
      var area = (maxx - minx) * (maxy - miny);
      if (area > bestArea) { bestArea = area; best = ring; }
    }
    if (!best) return null;
    var sx = 0, sy = 0;
    for (var j = 0; j < best.length; j++) { sx += best[j][0]; sy += best[j][1]; }
    return { lon: sx / best.length, lat: sy / best.length };
  }
  function setupSearch() {
    var input = $('glbSearch'), box = $('glbSearchResults'), activeIdx = -1, items = [], countryIndex = null;
    function close() { box.classList.remove('show'); input.setAttribute('aria-expanded', 'false'); activeIdx = -1; }
    function norm(s) { return (s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, ''); }
    function buildCountryIndex() {
      if (countryIndex || !countryFeats) return;
      countryIndex = [];
      for (var i = 0; i < countryFeats.length; i++) {
        var f = countryFeats[i], a2 = A.num2alpha2(+f.id), c = countryCentroid(f);
        if (c) countryIndex.push({ en: f.name || '', loc: a2 ? regionName(a2) : (f.name || ''), lat: c.lat, lon: c.lon, feat: f });
      }
    }
    input.addEventListener('input', function () {
      var q = norm(input.value.trim()); if (q.length < 2) { close(); return; }
      ensureCities().then(function () {
        var res = [];
        buildCountryIndex();
        if (countryIndex) for (var ci = 0; ci < countryIndex.length && res.length < 3; ci++) {
          var ct = countryIndex[ci];
          if (norm(ct.loc).indexOf(q) === 0 || norm(ct.en).indexOf(q) === 0)
            res.push({ kind: 'country', label: ct.loc, sub: '◎', lat: ct.lat, lon: ct.lon, feat: ct.feat });
        }
        if (cities) {
          var lim = cities.length;
          for (var i = 0; i < lim && res.length < 8; i++) {
            var nm = (cityNames && cityNames[i]) || cities[i][4];
            if (norm(nm).indexOf(q) === 0 || norm(cities[i][4]).indexOf(q) === 0)
              res.push({ kind: 'city', label: nm, sub: regionName(cities[i][2]), lat: cities[i][0], lon: cities[i][1] });
          }
          if (res.length < 4) for (var k = 0; k < lim && res.length < 8; k++) {
            var nm2 = (cityNames && cityNames[k]) || cities[k][4];
            if (norm(nm2).indexOf(q) > 0)
              res.push({ kind: 'city', label: nm2, sub: regionName(cities[k][2]), lat: cities[k][0], lon: cities[k][1] });
          }
        }
        items = res; renderResults();
      });
    });
    function renderResults() {
      if (!items.length) { box.innerHTML = '<div class="glb-sr-item">' + esc(tt('search_no_results', 'No matches')) + '</div>'; box.classList.add('show'); input.setAttribute('aria-expanded', 'true'); return; }
      box.innerHTML = items.map(function (r, idx) {
        return '<div class="glb-sr-item' + (idx === activeIdx ? ' active' : '') + '" data-idx="' + idx + '" role="option">' + esc(r.label) + '<small>' + esc(r.sub || '') + '</small></div>';
      }).join('');
      box.classList.add('show'); input.setAttribute('aria-expanded', 'true');
      Array.prototype.forEach.call(box.children, function (ch) {
        ch.addEventListener('mousedown', function (e) { e.preventDefault(); pick(+ch.getAttribute('data-idx')); });
      });
    }
    function pick(idx) {
      var r = items[idx]; if (!r) return;
      if (r.kind === 'country') highlightCountry(r.feat); else clearHighlight();
      flyTo(r.lat, r.lon, r.label, r.kind === 'city'); close(); input.blur(); ae('globe_search_flyto', { kind: r.kind });
    }
    input.addEventListener('keydown', function (e) {
      if (!box.classList.contains('show')) return;
      if (e.key === 'ArrowDown') { e.preventDefault(); activeIdx = Math.min(activeIdx + 1, items.length - 1); renderResults(); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); activeIdx = Math.max(activeIdx - 1, 0); renderResults(); }
      else if (e.key === 'Enter') { e.preventDefault(); pick(activeIdx >= 0 ? activeIdx : 0); }
      else if (e.key === 'Escape') { close(); }
    });
    document.addEventListener('click', function (e) { if (!box.contains(e.target) && e.target !== input) close(); });
  }

  // ── Controls (hand-rolled orbit) ────────────────────────────────────────
  function setupControls() {
    var lastX = 0, lastY = 0, pinchDist = 0, downX = 0, downY = 0, downMoved = 0;
    var pointers = {};
    function down(e) {
      aeFirstInteract();
      stage.classList.add('grabbing');
      pointers[e.pointerId] = { x: e.clientX, y: e.clientY };
      if (Object.keys(pointers).length === 1) { state.dragging = true; lastX = e.clientX; lastY = e.clientY; downX = e.clientX; downY = e.clientY; downMoved = 0; state.cam.vTheta = 0; state.cam.vPhi = 0; state.fly = null; }
      idleResumeAt = performance.now() + 4000;
      try { stage.setPointerCapture(e.pointerId); } catch (er) {}
    }
    function move(e) {
      if (pointers[e.pointerId]) pointers[e.pointerId] = { x: e.clientX, y: e.clientY };
      var ks = Object.keys(pointers);
      if (ks.length >= 2) {
        var a = pointers[ks[0]], b = pointers[ks[1]], dist = Math.hypot(a.x - b.x, a.y - b.y);
        if (pinchDist) { var dr = (pinchDist - dist) * 0.006; state.cam.radius = A.clamp(state.cam.radius + dr, DIST_MIN, DIST_MAX); }
        pinchDist = dist; hideTip(); return;
      }
      if (state.dragging) {
        var dx = e.clientX - lastX, dy = e.clientY - lastY; lastX = e.clientX; lastY = e.clientY;
        downMoved += Math.abs(dx) + Math.abs(dy);
        var spd = state.cam.radius * 0.0045;
        state.cam.theta -= dx * spd; state.cam.phi -= dy * spd;
        state.cam.vTheta = -dx * spd; state.cam.vPhi = -dy * spd;
        hideTip();
      } else { onHover(e); }
    }
    function up(e) {
      var wasSingle = Object.keys(pointers).length === 1, cx = e.clientX, cy = e.clientY;
      delete pointers[e.pointerId]; if (Object.keys(pointers).length < 2) pinchDist = 0;
      if (!Object.keys(pointers).length) {
        state.dragging = false; stage.classList.remove('grabbing'); idleResumeAt = performance.now() + 4000;
        if (wasSingle && downMoved < 8) onGlobeClick(cx, cy);   // a tap/click, not a drag
      }
    }
    stage.addEventListener('pointerdown', down);
    stage.addEventListener('pointermove', move);
    stage.addEventListener('pointerup', up);
    stage.addEventListener('pointercancel', up);
    stage.addEventListener('pointerleave', function (e) { if (!state.dragging) hideTip(); });
    stage.addEventListener('wheel', function (e) {
      e.preventDefault();
      state.cam.radius = A.clamp(state.cam.radius + (e.deltaY > 0 ? 1 : -1) * 0.18, DIST_MIN, DIST_MAX);
      idleResumeAt = performance.now() + 4000;
    }, { passive: false });

    // keyboard
    canvas.addEventListener('keydown', function (e) {
      var step = 0.12, handled = true;
      switch (e.key) {
        case 'ArrowLeft': state.cam.theta += step; break;
        case 'ArrowRight': state.cam.theta -= step; break;
        case 'ArrowUp': state.cam.phi -= step; break;
        case 'ArrowDown': state.cam.phi += step; break;
        case '+': case '=': state.cam.radius = A.clamp(state.cam.radius - 0.3, DIST_MIN, DIST_MAX); break;
        case '-': case '_': state.cam.radius = A.clamp(state.cam.radius + 0.3, DIST_MIN, DIST_MAX); break;
        case 'Home': resetView(); break;
        case ' ': toggleAutoRotate(); break;
        default: handled = false;
      }
      if (handled) { e.preventDefault(); idleResumeAt = performance.now() + 4000; state.fly = null; }
    });
    canvas.addEventListener('pointerdown', function () { try { canvas.focus({ preventScroll: true }); } catch (e) { canvas.focus(); } });
  }

  // ── UI wiring ───────────────────────────────────────────────────────────
  function setupUI() {
    setupControls(); setupSearch();
    // chips
    Array.prototype.forEach.call(document.querySelectorAll('#glbToggles .glb-chip'), function (chip) {
      var name = chip.getAttribute('data-layer');
      chip.setAttribute('aria-pressed', state.layers[name] ? 'true' : 'false');
      chip.addEventListener('click', function () {
        var on = chip.getAttribute('aria-pressed') !== 'true';
        chip.setAttribute('aria-pressed', on ? 'true' : 'false');
        setLayer(name, on);
      });
    });
    // initial layer visuals
    setLayer('night', state.layers.night); setLayer('clouds', state.layers.clouds); setLayer('atmosphere', state.layers.atmosphere);

    // style segmented
    var sr = $('glbStyleReal'), sst = $('glbStyleStyl');
    sr.addEventListener('click', function () { setStyle(0); sr.classList.add('active'); sst.classList.remove('active'); sr.setAttribute('aria-checked', 'true'); sst.setAttribute('aria-checked', 'false'); });
    sst.addEventListener('click', function () { setStyle(1); sst.classList.add('active'); sr.classList.remove('active'); sst.setAttribute('aria-checked', 'true'); sr.setAttribute('aria-checked', 'false'); });

    // auto-rotate
    $('glbAutoRotate').addEventListener('click', toggleAutoRotate);

    // locate me
    $('glbLocate').addEventListener('click', locateMe);

    // time scrubber + now + play + seasons
    var scrub = $('glbScrub'), nowBtn = $('glbNow'), playBtn = $('glbPlay');
    var playTimer = 0;
    function setPlaying(on) {
      if (on) {
        if (playTimer) return;
        if (state.clockMode === 'now') { state.clockMode = 'fixed'; state.fixedDate = getSimDate(); }
        nowBtn.classList.remove('active'); nowBtn.setAttribute('aria-pressed', 'false');
        playBtn.classList.add('active'); playBtn.setAttribute('aria-pressed', 'true');
        $('glbPlayLabel').textContent = tt('pause', 'Pause');
        $('glbPlayIcon').innerHTML = '<rect x="6" y="5" width="4" height="14"/><rect x="14" y="5" width="4" height="14"/>';
        playTimer = setInterval(function () {
          state.fixedDate = new Date(state.fixedDate.getTime() + 360000); // +6 min per tick
          updateSun(); refreshTimeVal(); if (moonGroup && moonGroup.visible) updateMoon();
          scrub.value = state.fixedDate.getUTCHours() * 60 + state.fixedDate.getUTCMinutes();
        }, 110);
        ae('globe_layer_toggle', { layer: 'timeplay', on: true });
      } else {
        if (playTimer) clearInterval(playTimer); playTimer = 0;
        playBtn.classList.remove('active'); playBtn.setAttribute('aria-pressed', 'false');
        $('glbPlayLabel').textContent = tt('play', 'Play');
        $('glbPlayIcon').innerHTML = '<polygon points="6 4 20 12 6 20"/>';
      }
    }
    playBtn.addEventListener('click', function () { setPlaying(playTimer === 0); });
    scrub.addEventListener('input', function () {
      setPlaying(false);
      var mins = +scrub.value; var d = new Date();
      d = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 0, 0, 0) + mins * 60000);
      state.clockMode = 'fixed'; state.fixedDate = d; nowBtn.classList.remove('active'); nowBtn.setAttribute('aria-pressed', 'false');
      updateSun(); refreshTimeVal(); if (moonGroup && moonGroup.visible) updateMoon();
    });
    nowBtn.addEventListener('click', function () {
      setPlaying(false);
      state.clockMode = 'now'; nowBtn.classList.add('active'); nowBtn.setAttribute('aria-pressed', 'true');
      scrub.value = (new Date().getUTCHours() * 60 + new Date().getUTCMinutes());
      updateSun(); refreshTimeVal();
    });
    Array.prototype.forEach.call(document.querySelectorAll('.glb-season'), function (b) {
      b.addEventListener('click', function () {
        setPlaying(false);
        var yr = new Date().getUTCFullYear(), s = b.getAttribute('data-season');
        var md = { mar: [2, 20], jun: [5, 21], sep: [8, 22], dec: [11, 21] }[s];
        state.clockMode = 'fixed';
        state.fixedDate = new Date(Date.UTC(yr, md[0], md[1], 12, 0, 0));
        nowBtn.classList.remove('active'); nowBtn.setAttribute('aria-pressed', 'false');
        scrub.value = 720; updateSun(); refreshTimeVal(); if (moonGroup && moonGroup.visible) updateMoon();
        rebuildAnalemmaIfOn();
      });
    });
    refreshTimeVal();

    // actions
    $('glbMeasure').addEventListener('click', function () { setMeasureMode(!measureMode); });
    $('glbShot').addEventListener('click', screenshot);
    $('glbPermalink').addEventListener('click', copyPermalink);
    $('glbReset').addEventListener('click', resetView);
    $('glbFs').addEventListener('click', toggleFullscreen);
    if (fsClose) fsClose.addEventListener('click', toggleFullscreen);

    // collapsible layers section
    var lh = $('glbLayersHead'), sec = $('glbLayersSection');
    if (lh) { lh.addEventListener('click', function () { sec.classList.toggle('collapsed'); lh.setAttribute('aria-expanded', sec.classList.contains('collapsed') ? 'false' : 'true'); }); lh.addEventListener('keydown', function (e) { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); lh.click(); } }); }

    // resize / visibility / offscreen
    if (window.ResizeObserver) new ResizeObserver(sizeRenderer).observe(stage);
    else window.addEventListener('resize', sizeRenderer);
    document.addEventListener('visibilitychange', function () { document.hidden ? stopLoop() : startLoop(); });
    if (window.IntersectionObserver) {
      new IntersectionObserver(function (es) { es.forEach(function (en) { en.isIntersecting ? startLoop() : stopLoop(); }); }, { threshold: 0.02 }).observe(stage);
    }
    document.addEventListener('fullscreenchange', function () { sizeRenderer(); });
    var mq = window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)');
    if (mq && mq.addEventListener) mq.addEventListener('change', function (e) { reduceMotion = e.matches; if (reduceMotion) { state.autoRotate = false; syncAutoRotateBtn(); } });
  }
  function rebuildAnalemmaIfOn() {
    if (!analemmaLine) return;
    scene.remove(analemmaLine);
    analemmaLine.traverse(function (o) { if (o.geometry) o.geometry.dispose(); });
    analemmaLine = null;
    if (state.layers.analemma) buildAnalemma();
  }
  function refreshTimeVal() {
    var el = $('glbTimeVal'); if (el) el.textContent = fmtDateUTC(getSimDate());
    var sc = $('glbScrub');
    if (sc) {
      var d = getSimDate(), m = d.getUTCHours() * 60 + d.getUTCMinutes();
      if (document.activeElement !== sc) sc.value = m;   // don't fight an active drag
      var pct = m / 1440 * 100;
      sc.style.background = 'linear-gradient(to var(--range-dir,right), var(--accent) ' + pct + '%, var(--border) ' + pct + '%)';
    }
  }

  function toggleAutoRotate() {
    state.autoRotate = !state.autoRotate; syncAutoRotateBtn();
    idleResumeAt = performance.now();
  }
  function syncAutoRotateBtn() {
    var b = $('glbAutoRotate'); if (!b) return;
    b.classList.toggle('active', state.autoRotate); b.setAttribute('aria-pressed', state.autoRotate ? 'true' : 'false');
  }
  function syncChips() {
    Array.prototype.forEach.call(document.querySelectorAll('#glbToggles .glb-chip'), function (chip) {
      chip.setAttribute('aria-pressed', state.layers[chip.getAttribute('data-layer')] ? 'true' : 'false');
    });
  }

  function resetView() {
    state.cam.theta = 2.4; state.cam.phi = 1.3; state.cam.radius = 2.7; state.cam.vTheta = 0; state.cam.vPhi = 0; state.fly = null;
    if (measureMode) setMeasureMode(false);
    clearHighlight();
    applyCamera(); announce(tt('reset_view', 'View reset'));
  }
  function locateMe() {
    // "Not supported" was the wrong story: Gecko hides the API behind the
    // geo.enabled pref, so the browser is modern and one setting is off.
    if (!navigator.geolocation) { if (window.__geo) __geo.notify('no_api'); return; }
    var btn = $('glbLocate'); btn.classList.add('active');
    navigator.geolocation.getCurrentPosition(function (p) {
      btn.classList.remove('active');
      flyTo(p.coords.latitude, p.coords.longitude, null, true);
      addCenterMarker(p.coords.latitude, p.coords.longitude);
      userLoc = { lat: p.coords.latitude, lon: p.coords.longitude };
      refreshISSPasses();
      ae('globe_locate', {});
    }, function (err) { btn.classList.remove('active'); if (window.__geo) __geo.notify(err); }, { timeout: 8000 });
  }
  function addCenterMarker(lat, lon) {
    if (centerMarker) scene.remove(centerMarker);
    var v = A.latLonToVec3(lat, lon, R * 1.01);
    centerMarker = new THREE.Mesh(new THREE.SphereGeometry(0.02, 12, 12), new THREE.MeshBasicMaterial({ color: 0xff5a8a }));
    centerMarker.position.set(v.x, v.y, v.z); scene.add(centerMarker);
  }

  // ── Screenshot / permalink / fullscreen ─────────────────────────────────
  function screenshot() {
    renderer.render(scene, camera);
    canvas.toBlob(function (blob) {
      if (!blob) { toast(tt('screenshot', 'Screenshot') + ' ✗', 'error'); return; }
      var name = 'earth-globe-' + Date.now() + '.png';
      try { window.__downloadFile ? window.__downloadFile(blob, name) : fallbackDownload(blob, name); }
      catch (e) { fallbackDownload(blob, name); }
      toast(tt('screenshot_saved', 'Image saved'), 'success'); ae('globe_screenshot', {});
    }, 'image/png');
  }
  function fallbackDownload(blob, name) { var a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = name; document.body.appendChild(a); a.click(); setTimeout(function(){ a.remove(); URL.revokeObjectURL(a.href); }, 120000); }

  function copyPermalink() {
    var c = state.cam, ll = screenCenterLatLon();
    var on = Object.keys(state.layers).filter(function (k) { return state.layers[k]; });
    var params = new URLSearchParams();
    if (ll) { params.set('lat', ll.lat.toFixed(2)); params.set('lon', ll.lon.toFixed(2)); }
    params.set('z', c.radius.toFixed(2));
    params.set('layers', on.join(','));
    if (state.style) params.set('style', '1');
    if (state.clockMode === 'fixed') params.set('t', '' + state.fixedDate.getTime());
    var url = location.origin + location.pathname + '?' + params.toString();
    var done = function () { toast(tt('permalink_copied', 'Link copied'), 'success'); ae('globe_permalink', {}); };
    if (navigator.clipboard) navigator.clipboard.writeText(url).then(done, function () { prompt('', url); });
    else prompt('', url);
  }
  function applyPermalinkOrTimezone() {
    var p = new URLSearchParams(location.search);
    if (p.has('lat') && p.has('lon')) {
      if (p.has('z')) state.cam.radius = A.clamp(parseFloat(p.get('z')) || 2.7, DIST_MIN, DIST_MAX);
      if (p.get('style') === '1') { setStyle(1); }
      if (p.has('t')) { var ts = parseInt(p.get('t'), 10); if (ts) { state.clockMode = 'fixed'; state.fixedDate = new Date(ts); } }
      if (p.has('layers')) {
        var on = p.get('layers').split(',');
        Object.keys(state.layers).forEach(function (k) { var want = on.indexOf(k) >= 0; if (want !== state.layers[k]) setLayer(k, want); });
      }
      var tp = targetThetaPhi(parseFloat(p.get('lat')), parseFloat(p.get('lon')));
      state.cam.theta = tp.theta; state.cam.phi = A.clamp(tp.phi, 0.12, Math.PI - 0.12);
      updateSun(); applyCamera();
      return;
    }
    // timezone auto-center (GitHub trick, no IP lookup)
    try {
      var offMin = -new Date().getTimezoneOffset(); // minutes east of UTC
      var lon = A.clamp(offMin / 4, -180, 180);
      var tp2 = targetThetaPhi(20, lon);
      state.cam.theta = tp2.theta; state.cam.phi = A.clamp(tp2.phi, 0.12, Math.PI - 0.12);
      applyCamera();
    } catch (e) {}
  }
  function toggleFullscreen() {
    if (document.fullscreenElement) { document.exitFullscreen && document.exitFullscreen(); }
    else { (stage.requestFullscreen && stage.requestFullscreen()) || (stage.webkitRequestFullscreen && stage.webkitRequestFullscreen()); }
    setTimeout(sizeRenderer, 120);
  }

  function setQualityLabel() {
    var el = $('glbQuality'); if (!el) return;
    var label = TIER === 'high' ? tt('quality_high', 'High') : TIER === 'mid' ? tt('quality_mid', 'Balanced') : tt('quality_low', 'Light');
    el.textContent = tt('quality', 'Quality') + ': ' + label + (degraded ? ' ·' : '');
  }
})();
